import { Box, FormControl, IconButton, InputLabel, Link, Stack, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import BackupIcon from '@mui/icons-material/Backup';
import { ReactNode } from 'react';
import { DownloadFileDto } from '@sr/dto';
import useConfirmDialog from 'hooks/confirm-dialog.hook';
import { FieldProps } from 'formik';
import { LinkToFile } from '../links/link-to-file';
import Alert from '@mui/material/Alert';
import { setAuthHeader } from '../../api/rtk-query';
import { LoadingBanner } from '../loading-banner';
import { useSnack } from '../snack';

const pickerTypes = ['image', 'doc', 'file'] as const;
type PickerTypes = typeof pickerTypes[number]

const uploadFile = (file: File, fileCategory: string, isPublicFile: boolean): Promise<DownloadFileDto> => {
  if (!file) throw new Error('No file provided for upload');
  const formData = new FormData();
  formData.append('file', file);
  formData.append('fileCategory', fileCategory);
  if (isPublicFile) {
    formData.append('isPublicFile', 'true');
  }

  const headers = new Headers();
  setAuthHeader(headers);

  return fetch('/api/files/upload', {
    method: 'POST',
    headers,
    body: formData
  })
    .then(async res => {
      if (!res.ok) {
        const errorText = await res.json().then(payload=> payload?.message ?? 'Неизвестная серверная ошибка');
        throw new Error(errorText);
      }
      return res.json();
    })
    .then((({ id, fileName, size })=>{
      if (!id || typeof id !== 'number') {
        throw new Error('Не удалось загрузить файл, не вернулся id');
      }
      if (!fileName || typeof fileName !== 'string') {
        throw new Error('Не удалось загрузить файл, не вернулось имя файла');
      }
      if (!size || typeof size !== 'number') {
        throw new Error('Не удалось загрузить файл, не вернулся размер файла');
      }
      return { id, fileName, size };
    }));
};


type PickerFieldProps =
  { label: ReactNode; disabled?: boolean; multiple?: boolean; pickerType?: PickerTypes; maxFileSize?: number, fileCategory: string, isPublicFile?: boolean }
  & FieldProps<DownloadFileDto[]>;

const FILE_SIZE_LIMIT = 15; // in Mb, default value

const getAllowedTypes = (type: PickerTypes): string[] => {
  let allowedTypes: string[] = [];
  switch (type){
  case 'image':
    allowedTypes.push(...IMAGE_TYPES, ...PDF_TYPES);
    break;
  case 'doc':
    allowedTypes.push(...DOCUMENT_TYPES);
    break;
  case 'file':
    allowedTypes.push(...IMAGE_TYPES, ...DOCUMENT_TYPES);
    break;
  default:
    throw Error(`File picker type "${type}" is not supported`);
  }
  return allowedTypes;
};

/**
 * @maxFileSize - размер файла в Mb
 * Образец использования свойства "pickerType"
 *                     <Field
 *                       name="offerFile"
 *                       label="Файл"
 *                       maxFileSize={10}   10-размер в Mb
 *                       pickerType="image"
 *                       component={SingleFilePickerField}
 *                       disabled={false}
 *                     />
 */

const FilePickerField = (props: PickerFieldProps) => {
  const {
    form: { setFieldValue, setFieldError, errors, setTouched, touched, validateField, setSubmitting, isSubmitting },
    field: { name, value },
    label,
    pickerType='file',
    fileCategory,
    maxFileSize = FILE_SIZE_LIMIT,
    isPublicFile = false,
    disabled = false,
    multiple = false
  } = props;

  if (!fileCategory){
    throw new Error('fileCategory is required for FilePickerField');
  }
  if (!multiple && value && value.length > 1) {
    throw new Error('Incorrect usage of single file picker');
  }

  const { showError } = useSnack();

  const validateFile = async (file: File)=> {
    if (file.size > maxFileSize * (1024 **2)) {
      setFieldError(name,
        `Размер файла: ${Math.floor(file.size/(1024**2))}Mb больше допустимого ${Math.floor((maxFileSize * (1024 **2))/1024**2)}Mb`);
      await setTouched({ [name]: true }, false);
      return false;
    }
    if (!getAllowedTypes(pickerType).includes(file.type)) {
      setFieldError(name, 'Данный формат файла не поддерживается');
      await setTouched({ [name]: true }, false);
      return false;
    }
    return true;
  };

  const onChange = async (event: any) => {
    const files = event?.target?.files;
    if (!files || !files.length) {
      console.warn('No files selected');
      return;
    }

    for (const file of files) {
      if (!await validateFile(file)) {
        console.warn('File validation failed', file);
        return;
      }
    }

    setSubmitting(true);

    const fileDtos: DownloadFileDto[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const fileDto = await uploadFile(files[i], fileCategory, isPublicFile);
        fileDtos.push(fileDto);
      }
    } catch (error) {
      showError(error instanceof Error && error.message ? error.message : 'Не удалось загрузить файл');
    } finally {
      setSubmitting(false);
    }

    if (multiple) {
      await setFieldValue(name, [...(value ? value : []), ...fileDtos]);
    } else {
      await setFieldValue(name, fileDtos);
    }
    await setTouched({ [name]: true }, false);
  };

  const showConfirm = useConfirmDialog();

  const handleClearClick = async (fileId: number) => {
    showConfirm({ content: 'Удалить файл?' }).then(async () => {
      const files = value?.filter(file=>file.id !== fileId );
      await setFieldValue(name, files);
    });
  };

  const acceptValue = (): string => {
    return pickerType === 'image'
      ? [...IMAGE_TYPES, ...PDF_TYPES].join(',')
      : pickerType === 'doc'?
        DOCUMENT_TYPES.join(',')
        : [...IMAGE_TYPES, ...DOCUMENT_TYPES].join(',');
  };

  const inputId = `file-input-${name}`;

  if (isSubmitting) {
    return <LoadingBanner/>;
  }

  return (
    <Box>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <FormControl>
          <InputLabel htmlFor="component-simple" shrink>{label}</InputLabel>
          <Box id="component-simple" minWidth={150} sx={{ p: 1, textOverflow: 'ellipsis' }}>
            {value && value.length
              ? <>
                {value.map((fileDto)=>
                  <Box key={fileDto.id}>
                    <LinkToFile key={fileDto.id} file={fileDto}/>
                    <IconButton
                      disabled={disabled}
                      onClick={() => handleClearClick(fileDto.id)}>
                      <HighlightOffIcon />
                    </IconButton>
                  </Box>
                )}
              </>
              : 
              <label htmlFor={inputId} style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <BackupIcon color="primary"/>
                  <Typography variant='body2' color="primary">
                    выбрать файл{multiple ? 'ы' : ''}...
                  </Typography>
                </Stack>
              </label>
            }
          </Box>
        </FormControl>
        <IconButton component='label' color='primary' disabled={disabled} htmlFor={inputId}>
          <AttachFileIcon />
        </IconButton>
      </Stack>

      <input 
        id={inputId}
        name="file" 
        accept={acceptValue()} 
        type="file" 
        onChange={onChange}
        multiple={multiple} 
        style={{ display: 'none' }} 
        disabled={disabled}
      />

      {touched[name] && errors[name] && (
        <Stack direction='row'>
          <Alert severity="error">
            { String(errors[name]) }
          </Alert>
          <IconButton
            disabled={disabled}
            onClick={() => {
              setFieldError(name, undefined);
            }}
          >
            <HighlightOffIcon />
          </IconButton>
        </Stack>
      )}
    </Box>
  );
};

export default FilePickerField;


// https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
const DOCUMENT_TYPES = [
  'application/pdf',
  'application/rtf',
  'text/plain',
  'text/csv', // .csv
  'application/zip', // .zip
  'application/msword', // .doc
  'application/vnd.ms-excel', // .xls
  'application/vnd.ms-powerpoint', // .ppt
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', //pptx
  'application/vnd.oasis.opendocument.presentation', // .odp
  'application/vnd.oasis.opendocument.spreadsheet', //ods
  'application/vnd.oasis.opendocument.text' //odt
];

const PDF_TYPES = [
  'application/pdf'
];

const IMAGE_TYPES = [
  'image/png',
  'image/heic',
  'image/jpeg',
  'image/gif',
  'image/tiff'
];
